import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Stack, Grid, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, IconButton, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Checkbox,
} from '@mui/material';
import {
  Search, Refresh, Add, Edit, MoreVert, Inventory2,
  Storefront, WarningAmber, ErrorOutline, CheckCircle,
  TrendingDown, ArrowUpward, ArrowDownward, TuneRounded,
  RestartAlt, RestoreFromTrash, Close as CloseIcon, History,
  SwapHoriz, DeleteOutline,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
  consumablesUrl, consumableByIdUrl, consumableStockUrl, loadInsurance,
} from '../BaseURL';

// ── Theme tokens (mirrors Procurement/StockAnalysis.js) ────────────────────
const PRIMARY = '#41a386';
const BORDER  = '#e5e9ef';
const MUTED   = '#64748b';
const STRONG  = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

const LEVEL = {
  critical: { color: '#dc2626', tint: 'rgba(220,38,38,0.10)', strong: '#991B1B', label: 'Out of stock', Icon: ErrorOutline },
  warning:  { color: '#d97706', tint: 'rgba(217,119,6,0.10)', strong: '#92400E', label: 'Low stock',    Icon: WarningAmber },
  ok:       { color: '#10b981', tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'In stock',    Icon: CheckCircle },
};

const stockLevel = (row) => {
  if (row.stock_qty <= 0) return 'critical';
  if (row.reorder_level > 0 && row.stock_qty <= row.reorder_level) return 'warning';
  return 'ok';
};

const StatusPill = ({ level }) => {
  const lv = LEVEL[level] || LEVEL.ok;
  const I = lv.Icon;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: lv.tint, color: lv.strong,
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <I sx={{ fontSize: 12 }} />
      {lv.label}
    </Box>
  );
};

const formatStock = (row) => {
  const { stock_qty = 0, pack_size = 1, pack_label, base_unit } = row;
  if (pack_size > 1 && stock_qty > 0) {
    const packs = Math.floor(stock_qty / pack_size);
    const loose = stock_qty % pack_size;
    const lbl   = (pack_label || 'pack').toLowerCase();
    const parts = [];
    if (packs > 0) parts.push(`${packs} ${lbl}${packs === 1 ? '' : 's'}`);
    if (loose > 0) parts.push(`${loose} ${base_unit}${loose === 1 ? '' : 's'}`);
    if (parts.length === 0) parts.push(`0 ${base_unit}`);
    return parts.join(' + ');
  }
  return `${stock_qty} ${base_unit}${stock_qty === 1 ? '' : 's'}`;
};

const StatTile = ({ label, value, icon, tint, color }) => (
  <Paper elevation={0} sx={{
    p: 1.5, borderRadius: 2,
    border: `1px solid ${BORDER}`,
    bgcolor: '#fff',
    display: 'flex', alignItems: 'center', gap: 1.5,
  }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: 1.5,
      bgcolor: tint, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ ...LABEL_SX, mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: STRONG, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

// ── Add / Edit dialog ──────────────────────────────────────────────────────
const ItemDialog = ({ open, onClose, mode, initial, onSaved }) => {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    name: '', category: '', base_unit: '', pack_label: '', pack_size: 1,
    reorder_level: 0, cost_per_unit: 0, description: '',
    initial_stock_packs: 0, initial_stock_loose: 0,
    selectedInsurances: [],
  });
  const [saving, setSaving] = useState(false);
  const [insuranceList, setInsuranceList] = useState([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);

  // Load insurance list when dialog opens (same source as the drug form).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setInsurancesLoading(true);
    axios.get(loadInsurance)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.results) ? res.data.results
                   : Array.isArray(res.data)         ? res.data
                   : [];
        setInsuranceList(list);
      })
      .catch(() => { if (!cancelled) setInsuranceList([]); })
      .finally(() => { if (!cancelled) setInsurancesLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Seed form when dialog opens. For edit mode, also fetch the
  // associatedInsurances from /consumables/:id so the checkboxes pre-tick.
  useEffect(() => {
    if (!open) return;
    setForm({
      name:                initial?.name || '',
      category:            initial?.category || '',
      base_unit:           initial?.base_unit || 'piece',
      pack_label:          initial?.pack_label || '',
      pack_size:           initial?.pack_size || 1,
      reorder_level:       initial?.reorder_level || 0,
      cost_per_unit:       initial?.cost_per_unit || 0,
      description:         initial?.description || '',
      initial_stock_packs: 0,
      initial_stock_loose: 0,
      selectedInsurances:  [],
    });
    if (isEdit && initial?.id) {
      axios.get(consumableByIdUrl(initial.id))
        .then((res) => {
          if (res.data?.success && Array.isArray(res.data.associatedInsurances)) {
            setForm((f) => ({ ...f, selectedInsurances: res.data.associatedInsurances }));
          }
        })
        .catch(() => {});
    }
  }, [open, initial, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInsurance = (insId) => {
    setForm((f) => ({
      ...f,
      selectedInsurances: f.selectedInsurances.includes(insId)
        ? f.selectedInsurances.filter((id) => id !== insId)
        : [...f.selectedInsurances, insId],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.base_unit.trim()) {
      Swal.fire({ icon: 'warning', title: 'Missing info', text: 'Name and base unit are required.' });
      return;
    }
    if (form.selectedInsurances.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Pick insurance', text: 'Select at least one insurance scheme this consumable is covered under.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        pack_size:     Math.max(1, parseInt(form.pack_size, 10) || 1),
        reorder_level: Math.max(0, parseInt(form.reorder_level, 10) || 0),
        cost_per_unit: parseFloat(form.cost_per_unit) || 0,
      };
      if (isEdit) {
        await axios.put(consumableByIdUrl(initial.id), payload);
      } else {
        await axios.post(consumablesUrl, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2.5, py: 1.5,
        bgcolor: 'rgba(65,163,134,0.10)', color: PRIMARY,
        borderBottom: `1px solid rgba(65,163,134,0.30)`,
      }}>
        <Inventory2 sx={{ fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
            Consumable
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
            {isEdit ? 'Edit item' : 'New item'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
        <Stack spacing={2}>

          {/* ── Section 1: Item identity ─────────────────────────────────
              Name + Category + Description all on one row. Description is
              single-line so the row heights match — long notes can still
              wrap horizontally within the field. */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER, bgcolor: '#fff', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 4, height: 18, borderRadius: 0.5, bgcolor: PRIMARY }} />
              <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Item identity</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" required label="Name"
                  placeholder="e.g. Cotton wool, Plaster, Cannula 22G"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Category"
                  placeholder="e.g. Wound Care"
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Description / notes"
                  placeholder="Optional notes for the storekeeper"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Section 2: Units & packaging ─────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER, bgcolor: '#fff', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 4, height: 18, borderRadius: 0.5, bgcolor: PRIMARY }} />
              <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Units & packaging</Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" required label="Base unit"
                  placeholder="piece, metre, roll…"
                  value={form.base_unit}
                  onChange={(e) => set('base_unit', e.target.value)}
                  helperText="Smallest unit you ever count" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Pack name"
                  placeholder="Bundle, Box, Carton…"
                  value={form.pack_label}
                  onChange={(e) => set('pack_label', e.target.value)}
                  helperText="Optional — blank for single units" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" type="number"
                  label="Pieces per pack"
                  value={form.pack_size}
                  onChange={(e) => set('pack_size', e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="e.g. 12 if 12 pieces fill a bundle" />
              </Grid>
            </Grid>
          </Paper>

          {/* ── Section 3: Stock & cost (+ initial stock when creating) ──
              Edit mode: 2 fields, each on half the row.
              Create mode: 4 fields in one row, 3/3/3/3, so the section
              never leaves dead space on the right. */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER, bgcolor: '#fff', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 4, height: 18, borderRadius: 0.5, bgcolor: PRIMARY }} />
              <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Stock & cost</Typography>
              {!isEdit && (
                <Typography sx={{ ...LABEL_SX, color: MUTED, ml: 'auto' }}>
                  Initial stock optional
                </Typography>
              )}
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={isEdit ? 6 : 3}>
                <TextField fullWidth size="small" type="number" label="Reorder level"
                  value={form.reorder_level}
                  onChange={(e) => set('reorder_level', e.target.value)}
                  inputProps={{ min: 0 }}
                  helperText={`Alert below this many ${form.base_unit || 'units'}`} />
              </Grid>
              <Grid item xs={12} sm={6} md={isEdit ? 6 : 3}>
                <TextField fullWidth size="small" type="number" label="Cost per base unit"
                  value={form.cost_per_unit}
                  onChange={(e) => set('cost_per_unit', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Updates on each purchase"
                  InputProps={{ startAdornment: <InputAdornment position="start">₵</InputAdornment> }} />
              </Grid>

              {!isEdit && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="number"
                      label={`Full ${(form.pack_label || 'pack').toLowerCase()}s on hand`}
                      value={form.initial_stock_packs}
                      onChange={(e) => set('initial_stock_packs', e.target.value)}
                      disabled={form.pack_size <= 1}
                      inputProps={{ min: 0 }}
                      helperText={form.pack_size > 1
                        ? `× ${form.pack_size} ${form.base_unit || 'pieces'} each`
                        : 'Define a pack above'} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth size="small" type="number"
                      label={`Loose ${form.base_unit || 'pieces'} on hand`}
                      value={form.initial_stock_loose}
                      onChange={(e) => set('initial_stock_loose', e.target.value)}
                      inputProps={{ min: 0 }}
                      helperText="Pieces not in a full pack" />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* ── Section 4: Insurance coverage ────────────────────────── */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER, bgcolor: '#fff', borderRadius: 2 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5, gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 4, height: 18, borderRadius: 0.5, bgcolor: PRIMARY }} />
                <Box>
                  <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Insurance coverage</Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                    Insurances ticked here appear in the Consumable Pricing matrix.
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Button size="small" disabled={saving || insurancesLoading || insuranceList.length === 0}
                  onClick={() => set('selectedInsurances', insuranceList.map((i) => i.id))}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: PRIMARY }}>
                  Select all
                </Button>
                <Button size="small" disabled={saving}
                  onClick={() => set('selectedInsurances', [])}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', color: MUTED }}>
                  Clear
                </Button>
              </Stack>
            </Stack>

            {insurancesLoading ? (
              <Box sx={{ textAlign: 'center', py: 3, color: MUTED }}>
                <CircularProgress size={20} sx={{ color: PRIMARY }} />
                <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                  Loading insurance providers…
                </Typography>
              </Box>
            ) : insuranceList.length === 0 ? (
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                <Typography sx={{ fontSize: '0.78rem', color: '#991B1B' }}>
                  No insurance providers found. Add them in <strong>Admin → Insurance Companies</strong> first.
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1,
              }}>
                {insuranceList.map((ins) => {
                  const checked = form.selectedInsurances.includes(ins.id);
                  return (
                    <Box key={ins.id}
                      onClick={() => !saving && toggleInsurance(ins.id)}
                      sx={{
                        px: 1, py: 0.75, borderRadius: 1.5, cursor: saving ? 'default' : 'pointer',
                        border: '1px solid', borderColor: checked ? PRIMARY : BORDER,
                        bgcolor: checked ? 'rgba(65,163,134,0.10)' : '#fff',
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        transition: 'all .12s ease',
                        '&:hover': { borderColor: PRIMARY, bgcolor: checked ? 'rgba(65,163,134,0.14)' : '#f8fafc' },
                      }}>
                      <Checkbox size="small" checked={checked} disabled={saving}
                        sx={{ p: 0.5, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />
                      <Typography sx={{
                        fontSize: '0.82rem',
                        fontWeight: checked ? 700 : 500,
                        color: checked ? '#2f8068' : STRONG,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {ins.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {form.selectedInsurances.length === 0 ? (
                <Typography sx={{ fontSize: '0.74rem', color: '#991B1B', fontWeight: 600 }}>
                  Pick at least one insurance scheme.
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '0.74rem', color: PRIMARY, fontWeight: 700 }}>
                  ✓ {form.selectedInsurances.length} of {insuranceList.length} scheme{insuranceList.length === 1 ? '' : 's'} selected
                </Typography>
              )}
            </Box>
          </Paper>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: MUTED }}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving} variant="contained" disableElevation
          startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Inventory2 sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: '#2f8068' } }}>
          {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create item')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Stock adjustment dialog ────────────────────────────────────────────────
const StockDialog = ({ open, onClose, item, currentUser, onSaved }) => {
  const [action, setAction] = useState('Receive');
  const [packs, setPacks]   = useState(0);
  const [loose, setLoose]   = useState(0);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAction('Receive');
      setPacks(0);
      setLoose(0);
      setReason('');
    }
  }, [open]);

  if (!item) return null;
  const packSize  = item.pack_size || 1;
  const hasPacks  = packSize > 1;
  const magnitude = (Math.max(0, parseInt(packs, 10) || 0) * packSize)
                  + Math.max(0, parseInt(loose, 10) || 0);
  const sign = (action === 'Receive' || action === 'Return') ? '+' : '-';
  const onHand = item.stock_qty;

  const handleSave = async () => {
    if (magnitude <= 0) {
      Swal.fire({ icon: 'warning', title: 'Quantity required', text: 'Enter at least 1 pack or 1 loose unit.' });
      return;
    }
    if (sign === '-' && magnitude > onHand) {
      Swal.fire({ icon: 'warning', title: 'Not enough on hand', text: `Only ${onHand} ${item.base_unit} on hand.` });
      return;
    }
    setSaving(true);
    try {
      await axios.post(consumableStockUrl(item.id), {
        action, packs, loose, reason, performedBy: currentUser || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err?.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const actionColor = sign === '+' ? '#15803D' : '#991B1B';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 2, py: 1.5,
        bgcolor: 'rgba(65,163,134,0.10)', color: PRIMARY,
        borderBottom: `1px solid rgba(65,163,134,0.30)`,
      }}>
        <TuneRounded sx={{ fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>Stock Adjustment</Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
            On hand: <strong style={{ color: STRONG }}>{formatStock(item)}</strong>
            {hasPacks ? ` (${packSize} ${item.base_unit}s per ${item.pack_label || 'pack'})` : ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.25, bgcolor: '#f8fafc' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select value={action} label="Action" onChange={(e) => setAction(e.target.value)}>
                <MenuItem value="Receive">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ArrowUpward sx={{ fontSize: 16, color: '#15803D' }} />
                    <span>Receive — incoming supply</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="Issue">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ArrowDownward sx={{ fontSize: 16, color: '#b45309' }} />
                    <span>Issue — to ward / department</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="Adjust">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <RestartAlt sx={{ fontSize: 16, color: MUTED }} />
                    <span>Adjust — stock-take correction</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="Damage">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningAmber sx={{ fontSize: 16, color: '#dc2626' }} />
                    <span>Damage / wastage</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="Return">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <RestoreFromTrash sx={{ fontSize: 16, color: '#0369a1' }} />
                    <span>Return — back from a ward</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.5, borderColor: BORDER, bgcolor: '#fff' }}>
              <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 1 }}>Quantity</Typography>
              <Grid container spacing={1.5}>
                {hasPacks && (
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" type="number"
                      label={`${item.pack_label || 'Pack'}${(item.pack_label || 'Pack').endsWith('s') ? '' : 's'}`}
                      value={packs}
                      onChange={(e) => setPacks(e.target.value)}
                      inputProps={{ min: 0 }}
                      helperText={`× ${packSize} ${item.base_unit}s each`}
                      InputProps={{ startAdornment: <InputAdornment position="start">{sign}</InputAdornment> }} />
                  </Grid>
                )}
                <Grid item xs={12} md={hasPacks ? 6 : 12}>
                  <TextField fullWidth size="small" type="number"
                    label={`Loose ${item.base_unit}s`}
                    value={loose}
                    onChange={(e) => setLoose(e.target.value)}
                    inputProps={{ min: 0 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">{sign}</InputAdornment> }} />
                </Grid>
              </Grid>
              <Box sx={{
                mt: 1.5, px: 1.25, py: 0.75, borderRadius: 1,
                bgcolor: sign === '+' ? 'rgba(16,185,129,0.10)' : 'rgba(220,38,38,0.10)',
                color: actionColor,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                  Net change
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800 }}>
                  {magnitude > 0 ? `${sign}${magnitude} ${item.base_unit}${magnitude === 1 ? '' : 's'}` : '—'}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Reason / reference (optional)"
              placeholder="e.g. Supplier GRN #4521, Ward request #88"
              value={reason}
              onChange={(e) => setReason(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: MUTED }}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || magnitude <= 0} variant="contained" disableElevation
          startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: '#2f8068' } }}>
          {saving ? 'Saving…' : 'Record adjustment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const Consumables = () => {
  const [rows, setRows]               = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter]     = useState('active');
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [dialogItem, setDialogItem] = useState(null);

  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockDialogItem, setStockDialogItem] = useState(null);

  // currentUser: tries to read from localStorage, falls back to ''
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.username || u?.name || '';
      }
    } catch (_) {}
    return '';
  }, []);

  // Debounce free-text search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(consumablesUrl, {
        params: {
          search:   search || undefined,
          category: categoryFilter || undefined,
          status:   statusFilter || undefined,
        },
        timeout: 15000,
      });
      if (data?.success) {
        setRows(data.results || []);
        setCategories(data.categories || []);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error('[consumables] load failed:', err.message || err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Computed stats ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = rows.length;
    const low   = rows.filter(r => r.stock_qty > 0 && r.reorder_level > 0 && r.stock_qty <= r.reorder_level).length;
    const out   = rows.filter(r => r.stock_qty <= 0).length;
    const value = rows.reduce((sum, r) => sum + (Number(r.stock_qty) || 0) * (Number(r.cost_per_unit) || 0), 0);
    return { total, low, out, value };
  }, [rows]);

  const openCreate = () => { setDialogMode('create'); setDialogItem(null); setDialogOpen(true); };
  const openEdit   = (row) => { setDialogMode('edit'); setDialogItem(row); setDialogOpen(true); };
  const openStock  = (row) => { setStockDialogItem(row); setStockDialogOpen(true); };

  const handleDeactivate = async (row) => {
    const res = await Swal.fire({
      icon: 'warning',
      title: 'Deactivate consumable?',
      html: `<p style="text-align:left;color:#475569;font-size:13px;">
               "${row.name}" will be hidden from active lists. Its stock history is preserved.
             </p>`,
      showCancelButton: true,
      confirmButtonText: 'Deactivate',
      confirmButtonColor: '#b91c1c',
      cancelButtonText: 'Cancel',
    });
    if (!res.isConfirmed) return;
    try {
      await axios.delete(consumableByIdUrl(row.id));
      fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    }
  };

  // Paginate client-side (lists are usually small enough)
  const pagedRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <Inventory2 sx={{ color: PRIMARY, fontSize: 18 }} />
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
              Procurement · Supplies
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            Consumables
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Cotton, plaster, cannula and other non-drug supplies. Quantities are tracked in <strong>{`base units (pieces, metres, rolls…)`}</strong>; record stock in mixed packs + loose pieces.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Reload">
            <span>
              <IconButton size="small" onClick={fetchData} disabled={loading}
                sx={{ border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED }}>
                {loading ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={openCreate}
            sx={{
              textTransform: 'none', fontWeight: 700,
              bgcolor: PRIMARY, '&:hover': { bgcolor: '#2f8068' },
            }}
          >
            New consumable
          </Button>
        </Stack>
      </Box>

      {/* Stat tiles */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatTile label="Total items" value={stats.total}
            icon={<Storefront sx={{ fontSize: 20 }} />} tint="rgba(65,163,134,0.10)" color={PRIMARY} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatTile label="Low stock" value={stats.low}
            icon={<WarningAmber sx={{ fontSize: 20 }} />} tint={LEVEL.warning.tint} color={LEVEL.warning.strong} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatTile label="Out of stock" value={stats.out}
            icon={<TrendingDown sx={{ fontSize: 20 }} />} tint={LEVEL.critical.tint} color={LEVEL.critical.strong} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatTile label="Stock value (₵)" value={stats.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            icon={<Inventory2 sx={{ fontSize: 20 }} />} tint="rgba(2,132,199,0.10)" color="#0369a1" />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name, category or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <MenuItem value=""><em>All categories</em></MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="low">Low stock</MenuItem>
              <MenuItem value="out">Out of stock</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Item</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Category</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Packaging</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Stock on hand</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Reorder</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Status</TableCell>
                <TableCell align="right" sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', color: MUTED }}>
                    <Inventory2 sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG }}>
                      No consumables to show
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                      {search || categoryFilter || statusFilter !== 'active'
                        ? 'Try adjusting the filters above.'
                        : 'Click "New consumable" to add the first item.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => {
                  const lvl = stockLevel(row);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                          {row.name}
                        </Typography>
                        {row.description && (
                          <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25, maxWidth: 360 }}>
                            {row.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        {row.category ? (
                          <Chip size="small" label={row.category}
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#f1f5f9', color: STRONG }} />
                        ) : (
                          <Typography sx={{ fontSize: '0.78rem', color: '#cbd5e1' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.2, fontSize: '0.78rem', color: STRONG }}>
                        {row.pack_size > 1
                          ? <>{row.pack_label || 'Pack'} <Typography component="span" sx={{ fontSize: '0.74rem', color: MUTED }}>· {row.pack_size} {row.base_unit}s/pack</Typography></>
                          : <Typography component="span" sx={{ fontSize: '0.78rem', color: MUTED }}>Single {row.base_unit}</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                          {formatStock(row)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>
                          {row.stock_qty} {row.base_unit}{row.stock_qty === 1 ? '' : 's'} total
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2, fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                        {row.reorder_level > 0 ? `${row.reorder_level} ${row.base_unit}s` : <Typography component="span" sx={{ color: '#cbd5e1' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <StatusPill level={lvl} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.2 }}>
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          {/* Mirrors the drug-side adjust icon (Home.js): same
                              purple SwapHoriz for stock movement entries. */}
                          <Tooltip title="Adjust stock (receive · issue · damage · return)" arrow>
                            <IconButton
                              size="small"
                              onClick={() => openStock(row)}
                              sx={{ color: '#8b5cf6', '&:hover': { bgcolor: 'rgba(139,92,246,0.10)' } }}>
                              <SwapHoriz sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit item" arrow>
                            <IconButton size="small" onClick={() => openEdit(row)}
                              sx={{ color: PRIMARY, '&:hover': { bgcolor: 'rgba(65,163,134,0.10)' } }}>
                              <Edit sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deactivate" arrow>
                            <IconButton size="small" onClick={() => handleDeactivate(row)}
                              sx={{ color: '#b91c1c', '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}>
                              <DeleteOutline sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Dialogs */}
      <ItemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode={dialogMode}
        initial={dialogItem}
        onSaved={() => fetchData()}
      />
      <StockDialog
        open={stockDialogOpen}
        onClose={() => setStockDialogOpen(false)}
        item={stockDialogItem}
        currentUser={currentUser}
        onSaved={() => fetchData()}
      />
    </Box>
  );
};

export default Consumables;
